import type { QueryClient } from '@tanstack/react-query';
import type { UserOrganization } from '../types/organization.types';
import type { OrganizationContextPort } from '../ports/organizationContext.port';
import { orgKeys } from './organization.keys';

export interface SwitchOrganizationParams {
  targetOrgId: string;
  previousOrgId?: string | null;
  userOrganizations: UserOrganization[];
  port: OrganizationContextPort;
  queryClient: QueryClient;
}

export const organizationSwitchService = {
  /**
   * Verifies that the user is an active member of the target organization.
   */
  validateMembership(targetOrgId: string, userOrganizations: UserOrganization[]): boolean {
    return userOrganizations.some(
      (uo) => uo.organization_id === targetOrgId && uo.is_active
    );
  },

  /**
   * Executes safe organization switching sequence:
   * 1. Validate membership.
   * 2. Set current organization in Supabase context (RPC).
   * 3. Cancel and remove cache queries belonging to previous organization.
   * 4. Invalidate and refetch core organization queries.
   */
  async switchOrganization({
    targetOrgId,
    previousOrgId,
    userOrganizations,
    port,
    queryClient,
  }: SwitchOrganizationParams): Promise<void> {
    const isMember = this.validateMembership(targetOrgId, userOrganizations);
    if (!isMember) {
      throw new Error('User is not an active member of the target organization');
    }

    // Call RPC to set context in DB session
    await port.setCurrentOrganization(targetOrgId);

    // Cancel in-flight queries and evict previous tenant cache if switching from an existing org
    if (previousOrgId && previousOrgId !== targetOrgId) {
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey;
          // Evict any query key that explicitly contains the previous org ID
          return key.some((part) => part === previousOrgId);
        },
      });
    }

    // Invalidate core org and tenant-dependent queries
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orgKeys.current() }),
      queryClient.invalidateQueries({ queryKey: orgKeys.currentRole(targetOrgId) }),
      queryClient.invalidateQueries({ queryKey: orgKeys.userRole(targetOrgId) }),
      queryClient.invalidateQueries({ queryKey: orgKeys.userOrgs() }),
    ]);
  },
};
